import { Fragment, useMemo } from "react";
import clinicAPI from "../../../services/clinicApi";
import s from "../clinic.module.css";
import { useToast } from "../../ui";
import {
  CALENDAR_HOURS,
  sessionColor,
  sessionsStartingInHour,
  weekGridSessionStyle,
} from "./calendarUtils";
import { useSessionGridDrag } from "./sessionDragDrop";
import SessionCalendarCard, { markSessionAllPresent } from "./SessionCalendarCard";

function daySessions(sessions, day) {
  return sessions
    .filter((sess) => sess.start_datetime?.slice(0, 10) === day)
    .sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime));
}

export default function WeekTimeGridView({
  days,
  sessions,
  groups,
  onOpenSession,
  onUpdated,
}) {
  const toast = useToast();

  const sessionsByDay = useMemo(() => {
    const map = {};
    for (const day of days) {
      map[day] = daySessions(sessions, day);
    }
    return map;
  }, [days, sessions]);

  const dnd = useSessionGridDrag({
    sessions,
    onReschedule: async (sess, patch) => {
      await clinicAPI.updateTrainingSession(sess.id, patch);
      const h = new Date(patch.start_datetime).getHours();
      const day = patch.start_datetime.slice(0, 10);
      toast.success(
        `Перенесено на ${day} ${String(h).padStart(2, "0")}:00`,
      );
      onUpdated?.();
    },
    onBlocked: (msg) => toast.error(msg),
  });

  const handleMarkAll = async (sess) => {
    try {
      await markSessionAllPresent(sess.id);
      toast.success("Все отмечены");
      onUpdated?.();
    } catch (e) {
      toast.error(
        e.code === "ATTENDANCE_NOT_OPEN"
          ? e.message
          : e.response?.data?.error || "Ошибка",
      );
    }
  };

  return (
    <div className={s.calWeekTimeGridWrap}>
      {dnd.dropHint ? (
        <div className={s.calDragHintBanner} role="status" aria-live="polite">
          {dnd.dropHint}
        </div>
      ) : null}
      <div
        className={s.calWeekTimeGrid}
        style={{
          gridTemplateColumns: `52px repeat(${days.length}, minmax(0, 1fr))`,
        }}
        onDragEnd={dnd.onDragEnd}
      >
      <div className={s.calWeekTimeCorner} />
      {days.map((day) => (
        <div
          key={`h-${day}`}
          className={`${s.calWeekTimeDayHead} ${day === new Date().toISOString().slice(0, 10) ? s.calWeekTimeDayHeadToday : ""}`}
        >
          <span className={s.calWeekTimeWeekday}>
            {new Date(`${day}T12:00:00`).toLocaleDateString("ru-RU", {
              weekday: "short",
            })}
          </span>
          <span className={s.calWeekTimeDate}>
            {new Date(`${day}T12:00:00`).toLocaleDateString("ru-RU", {
              day: "numeric",
              month: "short",
            })}
          </span>
        </div>
      ))}
      {CALENDAR_HOURS.map((hour) => (
        <Fragment key={hour}>
          <div className={s.calWeekTimeLabel}>
            {String(hour).padStart(2, "0")}:00
          </div>
          {days.map((day) => {
            const list = sessionsStartingInHour(
              sessionsByDay[day] || [],
              hour,
            );
            const cell = { day, hour };
            const isDrop = dnd.isDropTarget(cell);
            return (
              <div
                key={`${day}-${hour}`}
                className={`${s.calWeekTimeCell} ${isDrop ? s.calWeekTimeCellDrop : ""}`}
                onDragOver={(e) => dnd.overCell(e, cell)}
                onDrop={(e) => dnd.dropOnCell(e, cell)}
              >
                {list.map((sess) => {
                  const groupIdx = groups.findIndex(
                    (g) => g.id === sess.group?.id,
                  );
                  const color = sessionColor(sess, groupIdx);
                  const canDrag = dnd.canDragSession(sess);
                  const draggable =
                    sess.status !== "cancelled" &&
                    sess.status !== "completed";
                  const dragging = dnd.draggingId === sess.id;
                  return (
                    <div
                      key={sess.id}
                      draggable={draggable}
                      title={dnd.cardTitle(sess)}
                      className={`${s.calWeekTimeCardWrap} ${dragging ? s.calWeekTimeCardDragging : ""} ${!canDrag ? s.calHourCardLocked : ""}`}
                      style={weekGridSessionStyle(sess, hour)}
                      onDragStart={(e) => dnd.startDrag(e, sess)}
                      onDrag={dnd.onDrag}
                      onClickCapture={dnd.onClickCapture}
                    >
                      <SessionCalendarCard
                        sess={sess}
                        color={color}
                        variant="grid"
                        onClick={onOpenSession}
                        onMarkAll={handleMarkAll}
                      />
                    </div>
                  );
                })}
              </div>
            );
          })}
        </Fragment>
      ))}
      </div>
    </div>
  );
}
